import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { ClassAllocation } from '../../types/grid';
import { useLearningAreas } from '../../hooks/useLearningAreas';

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  curriculumAreas: { id: string; name: string }[];
  defaultClassMinutes: number;
  onCreateClass: (newClass: Omit<ClassAllocation, 'id'>) => Promise<void>;
}

const CreateClassModal: React.FC<CreateClassModalProps> = ({
  isOpen,
  onClose,
  curriculumAreas,
  defaultClassMinutes,
  onCreateClass
}) => {
  const [formData, setFormData] = useState({
    className: '',
    subjectCode: '',
    curriculumArea: '',
    yearLevel: '',
    level: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Canonical Learning Area names, merged with any areas passed in and the
  // currently selected value so a stored value outside the canonical list is
  // still selectable.
  const { learningAreaNames } = useLearningAreas();
  const areaOptions = useMemo(() => {
    const names = new Set<string>(learningAreaNames);
    curriculumAreas.forEach((area) => names.add(area.name));
    if (formData.curriculumArea) names.add(formData.curriculumArea);
    return Array.from(names);
  }, [learningAreaNames, curriculumAreas, formData.curriculumArea]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        className: '',
        subjectCode: '',
        curriculumArea: curriculumAreas.length > 0 ? curriculumAreas[0].name : '',
        yearLevel: '',
        level: ''
      });
      setErrors({});
    }
  }, [isOpen, curriculumAreas]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.className.trim()) {
      newErrors.className = 'Class name is required';
    }

    if (!formData.curriculumArea) {
      newErrors.curriculumArea = 'Learning area is required';
    }

    if (formData.yearLevel && isNaN(parseInt(formData.yearLevel))) {
      newErrors.yearLevel = 'Year level must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    setIsCreating(true);
    try {
      const newClass: Omit<ClassAllocation, 'id'> = {
        className: formData.className.trim(),
        curriculumArea: formData.curriculumArea,
        lineAssignments: {},
        classMinutes: defaultClassMinutes,
        // Write the canonical minutes field too. resolveClassMinutes() reads
        // minutesPerWeek ahead of any grid default, and every class the importer
        // produces carries it — a class created here that only had classMinutes
        // was the odd one out. See issue #227.
        minutesPerWeek: defaultClassMinutes,
        department: formData.curriculumArea,
        ...(formData.subjectCode && { subjectCode: formData.subjectCode.trim() }),
        ...(formData.yearLevel && { yearLevel: parseInt(formData.yearLevel) }),
        ...(formData.level && { level: formData.level.trim() })
      };

      await onCreateClass(newClass);
      onClose();
    } catch (error) {
      console.error('Failed to create class:', error);
      setErrors({ submit: 'Failed to create class. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="bg-navy-100 p-2 rounded-lg">
              <Plus className="h-6 w-6 text-navy-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-ink-900">
                Create New Class
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                Add a new class to the grid
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-500"
            disabled={isCreating}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error message */}
          {errors.submit && (
            <div className="bg-danger-bg border border-danger-border rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-danger-fg mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-danger-fg">{errors.submit}</div>
              </div>
            </div>
          )}

          {/* Required Fields */}
          <div>
            <h4 className="text-sm font-medium text-ink-700 mb-3">Required Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Class Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Class Name <span className="text-danger-fg">*</span>
                </label>
                <input
                  type="text"
                  value={formData.className}
                  onChange={(e) => setFormData({ ...formData, className: e.target.value })}
                  className={`block w-full border ${errors.className ? 'border-danger-border' : 'border-ink-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm`}
                  placeholder="e.g., Mathematics 10A"
                  autoFocus
                />
                {errors.className && (
                  <p className="text-xs text-danger-fg mt-1">{errors.className}</p>
                )}
              </div>

              {/* Learning Area */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Learning Area <span className="text-danger-fg">*</span>
                </label>
                <select
                  value={formData.curriculumArea}
                  onChange={(e) => setFormData({ ...formData, curriculumArea: e.target.value })}
                  className={`block w-full border ${errors.curriculumArea ? 'border-danger-border' : 'border-ink-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm`}
                >
                  <option value="">Select a learning area</option>
                  {areaOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {errors.curriculumArea && (
                  <p className="text-xs text-danger-fg mt-1">{errors.curriculumArea}</p>
                )}
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <div>
            <h4 className="text-sm font-medium text-ink-700 mb-3">Optional Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Subject Code */}
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Subject Code
                </label>
                <input
                  type="text"
                  value={formData.subjectCode}
                  onChange={(e) => setFormData({ ...formData, subjectCode: e.target.value })}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                  placeholder="e.g., MATH10"
                />
              </div>

              {/* Year Level */}
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Year Level
                </label>
                <input
                  type="number"
                  value={formData.yearLevel}
                  onChange={(e) => setFormData({ ...formData, yearLevel: e.target.value })}
                  className={`block w-full border ${errors.yearLevel ? 'border-danger-border' : 'border-ink-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm`}
                  placeholder="e.g., 10"
                  min="7"
                  max="12"
                />
                {errors.yearLevel && (
                  <p className="text-xs text-danger-fg mt-1">{errors.yearLevel}</p>
                )}
              </div>

              {/* Level */}
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Level
                </label>
                <input
                  type="text"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                  placeholder="e.g., Discover, Explore, Focus"
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-navy-50 border border-navy-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-navy-800 mb-2">What happens when you create a class?</h4>
            <ul className="text-sm text-navy-700 space-y-1 list-disc list-inside">
              <li>A new class will be added to the grid with no line assignments</li>
              <li>Class minutes will be set to {defaultClassMinutes} (grid default)</li>
              <li>The class will be tagged with the selected learning area</li>
              <li>You can assign teachers to lines after creation</li>
              <li>The class will be saved to the database immediately</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!formData.className.trim() || !formData.curriculumArea || isCreating}
            loading={isCreating}
          >
            Create Class
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateClassModal;
